import { useEffect, useRef } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';

interface FeedLightboxProps {
  open: boolean;
  slides: { src: string }[];
  index: number;
  onClose: () => void;
}

const HINT_KEY = 'wsj_feed_lightbox_hint_shown';

export function FeedLightbox({ open, slides, index, onClose }: FeedLightboxProps): React.JSX.Element {
  const hintRef = useRef<HTMLDivElement>(null);

  // Show keyboard hint on first lightbox open, then auto-dismiss
  useEffect(() => {
    if (!open) return;
    if (localStorage.getItem(HINT_KEY)) return;

    localStorage.setItem(HINT_KEY, '1');

    const el = hintRef.current;
    if (el) el.style.display = 'flex';

    const dismiss = (): void => {
      if (el) el.style.display = 'none';
    };
    const timer = setTimeout(dismiss, 3000);
    window.addEventListener('keydown', dismiss, { once: true });
    window.addEventListener('click', dismiss, { once: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', dismiss);
      window.removeEventListener('click', dismiss);
    };
  }, [open]);

  return (
    <>
      <Lightbox
        open={open}
        close={onClose}
        slides={slides}
        index={index}
        plugins={[Zoom, Counter]}
        zoom={{
          scrollToZoom: true,
          maxZoomPixelRatio: 3,
        }}
        carousel={{
          finite: true,
        }}
        styles={{
          container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
        }}
      />
      <div
        ref={hintRef}
        className="fixed inset-0 z-[10000] items-center justify-center pointer-events-none"
        style={{ display: 'none' }}
      >
        <div className="bg-black/70 text-white text-sm px-6 py-4 rounded-lg text-center space-y-1">
          <p>Use arrow keys to navigate</p>
          <p>Scroll to zoom</p>
          <p>Esc to close</p>
        </div>
      </div>
    </>
  );
}
